import { EventRegistryProxy } from 'app/shared/events/EventRegistryProxy';
import { ViewComponentModel, IndicatorManager } from 'app/views/accessories/IndicatorManager';

export class IndicatorManagerProxy implements IndicatorManager {
    private _indicatorManager: IndicatorManager;
    private _isDestroyed: boolean;
    private readonly eventRegistryProxy: EventRegistryProxy;

    /**
     * Acts as a proxy to another indicator manager.
     * This indicator manager will always return NO indicators until it has been proxied to another indicator manager at some
     * point in the future. Useful when you can't construct your indicator manager straight away.
     * Avoids the need for a null pointer to an indicator manager for an indefinite amount of time,
     * or initialization methods inside the indicator managers.
     * You can also swap out the proxied indicator manager at any point more than once and all listeners will be transferred.
     */
    constructor() {
        this.eventRegistryProxy = new EventRegistryProxy();
        this._isDestroyed = false;
    }

    /**
     * Turns this indicator manager
     * @param indicatorManager
     */
    proxyTo(indicatorManager: IndicatorManager) {
        if (indicatorManager === this._indicatorManager) {
            throw new Error(`You're already proxying here!`);
        }

        // destroy our last indicator manager if it's there!
        this.destroy();

        // setup our new one
        this._isDestroyed = false;
        this._indicatorManager = indicatorManager;

        // make a little event registry interface wrapper for the indicator manager.
        this.eventRegistryProxy.setTarget({
            addEventListener: (fn) => {
                return this._indicatorManager.onChange(fn as any);
            },
            notifyListeners: () => {
                throw new Error(`this shouldn't happen!`);
            },
        });
    }

    getCurrentIndicators(assetId: number): Array<ViewComponentModel> {
        return this._indicatorManager ? this._indicatorManager.getCurrentIndicators(assetId) : [];
    }

    onChange(fn: () => void): () => void {
        if (this._isDestroyed) {
            throw new Error('Indicator Manager already destroyed, can not call this method!');
        }
        return this.eventRegistryProxy.addEventListener(fn);
    }

    destroy() {
        if (!this._isDestroyed && this._indicatorManager) {
            this._isDestroyed = true;
            this._indicatorManager.destroy();
        }
    }
}
